/**
 * WorkflowBuilder — visual drag-free builder for approval workflow definitions.
 *
 * Allows admins to:
 *  - Set workflow name, type, and deadline
 *  - Choose SEQUENTIAL / PARALLEL / MAJORITY pattern
 *  - Add/remove/reorder steps, assign approver role per step
 *  - Save the definition (POST /v1/workflows/definitions)
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authHeaders, errorMessage, showAdminError } from './adminApi';
import { tv } from './i18nUtil';

interface WorkflowStep {
  label:         string;
  assigneeRole?: string;
  assigneeId?:   string;
}

interface WorkflowDefinition {
  id:            string;
  name:          string;
  workflow_type: string;
  pattern:       string;
  deadline_hours: number;
  is_active:     boolean;
  created_at:    string;
}

type Pattern = 'SEQUENTIAL' | 'PARALLEL' | 'MAJORITY';

const WORKFLOW_TYPES = [
  'PROCUREMENT', 'LEGAL', 'DATA_ACCESS', 'ROOM_CREATE',
  'FEDERATION', 'GDPR_ERASURE', 'CUSTOM',
];

/** English defaults for t('adminUi.workflow.pattern<Name>') */
const PATTERN_LABELS: Record<Pattern, string> = {
  SEQUENTIAL: 'Sequential (one approver at a time)',
  PARALLEL:   'Parallel (all at once)',
  MAJORITY:   'Majority (quorum required)',
};

async function apiFetch<T>(url: string, opts?: RequestInit): Promise<T> {
  // session token + Content-Type only with a body (see adminApi.ts); failed actions are shown
  const headers: Record<string, string> = { ...authHeaders(), ...(opts?.body != null ? { 'Content-Type': 'application/json' } : {}), ...(opts?.headers as Record<string, string> | undefined) };
  const res = await fetch(url, { ...opts, headers });
  if (!res.ok) {
    const msg = await errorMessage(res);
    if ((opts?.method ?? 'GET') !== 'GET') showAdminError(msg);
    throw new Error(msg);
  }
  return (await res.json() as { data: T }).data;
}

export function WorkflowBuilder() {
  const { t } = useTranslation();
  const qc    = useQueryClient();

  const [showForm, setShowForm]           = useState(false);
  const [name, setName]                   = useState('');
  const [wfType, setWfType]               = useState('CUSTOM');
  const [pattern, setPattern]             = useState<Pattern>('SEQUENTIAL');
  const [deadlineHours, setDeadlineHours] = useState(72);
  const stepLabel = (n: number) => tv(t, 'adminUi.workflow.defaultStep', 'Approval Step {{n}}', { n });
  const patternKey: Record<Pattern, string> = {
    SEQUENTIAL: 'adminUi.workflow.patternSequential',
    PARALLEL:   'adminUi.workflow.patternParallel',
    MAJORITY:   'adminUi.workflow.patternMajority',
  };
  const [steps, setSteps]                 = useState<WorkflowStep[]>(() => [{ label: stepLabel(1), assigneeRole: 'ADMIN' }]);
  const [formError, setFormError]         = useState<string | null>(null);

  const defsQ = useQuery({
    queryKey: ['workflow-defs'],
    queryFn:  () => apiFetch<{ definitions: WorkflowDefinition[] }>('/v1/workflows/definitions'),
  });

  const createMutation = useMutation({
    mutationFn: (body: unknown) =>
      apiFetch('/v1/workflows/definitions', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workflow-defs'] });
      setShowForm(false);
      setName('');
      setSteps([{ label: stepLabel(1), assigneeRole: 'ADMIN' }]);
      setFormError(null);
    },
    onError: (err: Error) => setFormError(err.message),
  });

  const addStep = () =>
    setSteps(s => [...s, { label: stepLabel(s.length + 1), assigneeRole: 'ADMIN' }]);

  const removeStep = (idx: number) =>
    setSteps(s => s.filter((_, i) => i !== idx));

  const updateStep = (idx: number, patch: Partial<WorkflowStep>) =>
    setSteps(s => s.map((step, i) => i === idx ? { ...step, ...patch } : step));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-nord-4">{t('adminUi.workflow.title', 'Workflow Definitions')}</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="rounded-lg bg-nord-frost-1 px-4 py-2 text-sm font-semibold text-nord-0 hover:bg-nord-frost-2"
        >
          {t('adminUi.workflow.new', '+ New Definition')}
        </button>
      </div>

      {/* Create form */}
      {showForm && (
        <div className="rounded-lg border border-nord-2 bg-nord-1 p-5 flex flex-col gap-4">
          <h3 className="text-sm font-semibold text-nord-4">{t('adminUi.workflow.build', 'Build Workflow')}</h3>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-nord-muted">{t('adminUi.workflow.name', 'Name')}</label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder={t('adminUi.workflow.namePlaceholder', 'e.g. Procurement Approval')}
                className="w-full rounded-lg border border-nord-3 bg-nord-0 px-3 py-2 text-sm text-nord-4 placeholder-nord-muted focus:border-nord-frost-1 focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-nord-muted">{t('adminUi.workflow.type', 'Type')}</label>
              <select
                value={wfType}
                onChange={e => setWfType(e.target.value)}
                className="w-full rounded-lg border border-nord-3 bg-nord-0 px-3 py-2 text-sm text-nord-4 focus:border-nord-frost-1 focus:outline-none"
              >
                {WORKFLOW_TYPES.map(tt => <option key={tt} value={tt}>{tt}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-nord-muted">{t('adminUi.workflow.pattern', 'Pattern')}</label>
              <select
                value={pattern}
                onChange={e => setPattern(e.target.value as Pattern)}
                className="w-full rounded-lg border border-nord-3 bg-nord-0 px-3 py-2 text-sm text-nord-4 focus:border-nord-frost-1 focus:outline-none"
              >
                {(Object.keys(PATTERN_LABELS) as Pattern[]).map(p => (
                  <option key={p} value={p}>{t(patternKey[p], PATTERN_LABELS[p])}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-nord-muted">
                {t('adminUi.workflow.deadline', 'Deadline (hours)')}
              </label>
              <input
                type="number"
                min={1}
                max={8760}
                value={deadlineHours}
                onChange={e => setDeadlineHours(Number(e.target.value))}
                className="w-full rounded-lg border border-nord-3 bg-nord-0 px-3 py-2 text-sm text-nord-4 focus:border-nord-frost-1 focus:outline-none"
              />
            </div>
          </div>

          {/* Steps builder */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="text-xs font-medium text-nord-muted">
                {tv(t, 'adminUi.workflow.steps', 'Approval Steps ({{n}})', { n: steps.length })}
              </label>
              <button
                onClick={addStep}
                className="text-xs text-nord-frost-1 hover:underline"
              >
                {t('adminUi.workflow.addStep', '+ Add step')}
              </button>
            </div>
            <div className="flex flex-col gap-2">
              {steps.map((step, idx) => (
                <div key={idx} className="flex items-center gap-3 rounded-lg bg-nord-0 p-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-nord-frost-1/20 text-xs font-bold text-nord-frost-1">
                    {idx + 1}
                  </span>
                  <input
                    type="text"
                    value={step.label}
                    onChange={e => updateStep(idx, { label: e.target.value })}
                    className="flex-1 rounded border border-nord-3 bg-nord-1 px-2 py-1 text-sm text-nord-4 focus:border-nord-frost-1 focus:outline-none"
                  />
                  <input
                    type="text"
                    value={step.assigneeRole ?? ''}
                    onChange={e => updateStep(idx, { assigneeRole: e.target.value })}
                    placeholder={t('adminUi.workflow.rolePlaceholder', 'Role (e.g. ADMIN)')}
                    className="w-32 rounded border border-nord-3 bg-nord-1 px-2 py-1 text-sm text-nord-4 placeholder-nord-muted focus:border-nord-frost-1 focus:outline-none"
                  />
                  {steps.length > 1 && (
                    <button
                      onClick={() => removeStep(idx)}
                      className="text-nord-11 hover:text-nord-11/80 text-sm"
                      aria-label={t('adminUi.workflow.removeStep', 'Remove step')}
                      title={t('adminUi.workflow.removeStep', 'Remove step')}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {formError && <p className="text-sm text-nord-11">{formError}</p>}

          <div className="flex justify-end gap-2">
            <button onClick={() => setShowForm(false)} className="text-sm text-nord-muted hover:text-nord-4">
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              onClick={() => createMutation.mutate({
                name, workflowType: wfType, pattern, deadlineHours, steps,
              })}
              disabled={createMutation.isPending || !name || steps.length === 0}
              className="rounded-lg bg-nord-frost-1 px-4 py-2 text-sm font-semibold text-nord-0 hover:bg-nord-frost-2 disabled:opacity-50"
            >
              {createMutation.isPending ? t('adminUi.saving', 'Saving…') : t('adminUi.workflow.save', 'Save Workflow')}
            </button>
          </div>
        </div>
      )}

      {/* Definitions list */}
      {defsQ.isLoading && <p className="text-sm text-nord-muted">{t('common.loading', 'Loading…')}</p>}
      <div className="flex flex-col gap-2">
        {defsQ.data?.definitions.map(def => (
          <div key={def.id} className="rounded-lg border border-nord-2 bg-nord-1 p-4">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-sm font-semibold text-nord-4">{def.name}</p>
                <p className="mt-0.5 text-xs text-nord-muted">
                  {def.workflow_type} · {def.pattern} · {def.deadline_hours}h
                </p>
              </div>
              <span className={`rounded px-2 py-0.5 text-xs font-semibold ${def.is_active ? 'bg-nord-14/20 text-nord-14' : 'bg-nord-2 text-nord-muted'}`}>
                {def.is_active ? t('adminUi.active', 'Active') : t('adminUi.inactive', 'Inactive')}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
